import React, {lazy, Suspense} from 'react'
import {Provider} from 'react-redux'
import {Router, Route, Switch} from 'react-router-dom'
import {ThemeProvider} from 'styled-components'
import {createBrowserHistory} from 'history'
import axios from 'axios'
import Validator from 'ree-validate'
import moment from 'moment'
import {Provider as AlertProvider} from 'react-alert'

import Layout from './components/Layout'
import GlobalStyles from 'core/styles/GlobalStyles'
import AlertTemplate from 'core/components/AlertTemplate'
import * as store from './store'
import {theme} from 'core/theme'
import IdleHandler from 'core/components/IdleHandler'
import LazyFallback from 'core/components/LazyFallback'
import PuppyChecklist from './scenes/PuppyChecklist'
import PuppyChoose from './scenes/PuppyChoose'
import PuppyQuestions from './scenes/PuppyQuestions'
import PuppyReady from './scenes/PuppyReady'
import PuppyResources from './scenes/PuppyResources'

// Home pages
const Home = lazy(() => import('./scenes/Home'))
const HomePuppies = lazy(() => import('./scenes/HomePuppies'))
const HomeGroomers = lazy(() => import('./scenes/HomeGroomers'))
const HomeTrainers = lazy(() => import('./scenes/HomeTrainers'))

// Search pages
const SearchPuppies = lazy(() => import('./scenes/SearchPuppies'))
const SearchGroomers = lazy(() => import('./scenes/SearchGroomers'))
const SearchTrainers = lazy(() => import('./scenes/SearchTrainers'))

const LocalPages = lazy(() => import('./scenes/LocalPages'))

// Advertise pages
const Advertise = lazy(() => import('./scenes/Advertise'))
const AdvertiseBreeder = lazy(() => import('./scenes/AdvertiseBreeder'))
const AdvertiseTrainer = lazy(() => import('./scenes/AdvertiseTrainer'))
const AdvertiseGroomers = lazy(() => import('./scenes/AdvertiseGroomer'))
const AdvertiseParentClubPromotion = lazy(() => import('./scenes/AdvertiseParentClubPromotion'))
const AdvertiseCanineCollege = lazy(() => import('./scenes/AdvertiseCanineCollege'))
const AdvertisePuppyVisor = lazy(() => import('./scenes/AdvertisePuppyVisor'))

// Terms of Service
const PuppyVisorTOS = lazy(() => import('./scenes/PuppyVisorTos'))
const CanineCollegeTOS = lazy(() => import('./scenes/CanineCollegeTos'))

// Vertical pages
const Breeder = lazy(() => import('./scenes/Breeder'))
const BreederListing = lazy(() => import('./scenes/BreederListing'))
const Groomer = lazy(() => import('./scenes/Groomer'))
const Trainer = lazy(() => import('./scenes/Trainer'))

// About pages
const Static = lazy(() => import('./scenes/Static'))
const StaticAbout = lazy(() => import('./scenes/StaticAbout'))

// States page
const SeoLinks = lazy(() => import('./scenes/SeoLinks'))

// Don't get scammed
const StaySafeBreeders = lazy(() => import('./scenes/StaySafeBreeders'))

// Errors
const Error404 = lazy(() => import('./scenes/Error404'))
const AdvertiseTos = lazy(() => import('web/scenes/AdvertiseTos'))

const tokenElement = document.querySelector('meta[name=csrf-token]')
if (tokenElement) {
  axios.defaults.headers.common['X-CSRF-TOKEN'] = tokenElement.content
}

Validator.extend('date_between', function (value, rules) {
  return moment(value.date).isBetween(rules[0], rules[1], rules[3])
})

Validator.extend('date_format', function (value, rules) {
  return moment(value.date, rules[0]).format(rules[0]) === value.date
})

const alertProviderOptions = {
  position: 'bottom right',
  timeout: 8000,
  offset: '30px',
  transition: 'fade',
  containerStyle: {
    zIndex: theme.zIndex.alert
  }
}

const App = () => (
  <Provider store={store.default(window.marketplace.state)}>
    <IdleHandler/>
    <ThemeProvider theme={theme}>
      <React.Fragment>
        <GlobalStyles />
        <AlertProvider template={AlertTemplate} {...alertProviderOptions}>
          <Router history={createBrowserHistory()}>
            <Layout>
            <Suspense fallback={<LazyFallback />}>
              <Switch>
                <Route component={Home} path='/' exact />
                <Route component={HomePuppies} path='/search-puppies' exact />
                <Route component={HomeGroomers} path='/search-groomers' exact />
                <Route component={HomeTrainers} path='/search-trainers' exact />
                <Route component={SearchPuppies} path='/puppies/:breed_name/:state_name/:city_name/:query?' exact />
                <Route component={SearchPuppies} path='/puppies/:breed_name/:state_name/:query?' exact />
                <Route component={SearchPuppies} path='/puppies/:query?' exact />
                <Route component={SearchGroomers} path='/groomers/:query?' exact />
                <Route component={SearchTrainers} path='/trainers/:query?' exact />
                <Route component={LocalPages} path='/puppies/local/:location_slug' exact />
                <Route component={Advertise} path='/advertise' exact />
                <Route component={AdvertiseBreeder} path='/advertise/breeders' exact />
                <Route component={AdvertiseTrainer} path='/advertise/trainers' exact />
                <Route component={AdvertiseGroomers} path='/advertise/groomers' exact />
                <Route component={AdvertiseParentClubPromotion} path='/advertise/breeder-parent-club-promotion' exact />
                <Route component={AdvertiseCanineCollege} path='/advertise/groomer-canine-college/:hash' exact />
                <Route component={AdvertisePuppyVisor} path='/puppyvisor' exact />
                <Route component={PuppyVisorTOS} path='/puppyvisor/termsofservice' exact />
                <Route component={CanineCollegeTOS} path='/advertise/caninecollege/termsofservice' exact />
                <Route component={AdvertiseTos} path='/advertise/policy' exact />
                <Route component={BreederListing} path='/breeder/:breeder/:breed/:id' exact />
                <Route component={Breeder} path='/breeder/:breeder' exact />
                <Route component={Groomer} path='/groomer/:groomer' exact />
                <Route component={Trainer} path='/trainer/:trainer' exact />
                <Route component={Static} path='/akc-good-works' exact />
                <Route component={PuppyChecklist} path='/preparing-for-puppy' exact />
                <Route component={PuppyChoose} path='/choosing-a-breed' exact />
                <Route component={PuppyQuestions} path='/questions-to-ask-breeder' exact />
                <Route component={PuppyResources} path='/about-akc-marketplace' exact />
                <Route component={PuppyReady} path='/things-to-consider-before-getting-a-dog' exact />
                <Route component={Static} path='/breeders-faq' exact />
                <Route component={Static} path='/about-akc-groomerfinder' exact />
                <Route component={Static} path='/about-akc-puppyfinder' exact />
                <Route component={Static} path='/find-groomer-for-dog' exact />
                <Route component={Static} path='/find-puppies-the-right-homes' exact />
                <Route component={Static} path='/buyers-protect-yourself' exact />
                <Route component={Static} path='/puppy-owner-checklist' exact />
                <Route component={Static} path='/glossary' exact />
                <Route component={StaticAbout} path='/termsofservice' exact />
                <Route component={StaticAbout} path='/privacy' exact />
                <Route component={SeoLinks} path='/states' exact />
                <Route component={SeoLinks} path='/breeds' exact />
                <Route component={StaySafeBreeders} path='/stay-safe-breeders' exact />
                <Route component={Error404} />
              </Switch>
              </Suspense>
            </Layout>
          </Router>
        </AlertProvider>
      </React.Fragment>
    </ThemeProvider>
  </Provider>
)

export default App
